import { useCallback } from 'react'
import { create } from 'zustand'
import { useAuthStore } from './auth'

/**
 * Черновики по каналам. Чат пересоздаётся при смене канала (key={channelId}), и текст
 * из его состояния пропадал. Здесь черновик живёт, пока не отправлен, и переживает
 * перезагрузку: localStorage, отдельно для каждой учётной записи. Только текст; файлы
 * черновика — в draftFiles.ts (в памяти вкладки), ответ в черновик не входит.
 */

const KEY = 'klichat.drafts.'
/** localStorage общий на весь сайт и небольшой: помним последние беседы и не больше этого текста. */
const MAX_CHANNELS = 50
const MAX_CHARS = 100_000

interface DraftsState {
  /** id канала → текст; пустых черновиков нет. */
  drafts: Record<string, string>
  setDraft: (channelId: string, text: string) => void
}

/** Чьи черновики сейчас в памяти. */
let owner: string | null = null

/**
 * Упоминания из подсказки: в поле видно «@Ник», а на сервер уходит <@id>. Без этой пары
 * вернувшийся черновик отправил бы упоминание простым текстом. Карту по каналу меняет
 * useMentions, сохраняются из неё только пары, которые ещё есть в тексте.
 */
const picks = new Map<string, Map<string, string>>()

export const useDraftsStore = create<DraftsState>((set) => ({
  drafts: {},
  setDraft: (channelId, text) => {
    set((s) => {
      if ((s.drafts[channelId] ?? '') === text) return s
      const drafts = { ...s.drafts }
      // удаляем и кладём заново: порядок ключей — от давних к свежим, лишнее срезается с начала.
      // id каналов — длинные числа, и для таких ключей объект хранит порядок вставки
      // (числовые ключи до 2³² он сортировал бы)
      delete drafts[channelId]
      if (text) drafts[channelId] = text
      return { drafts }
    })
    schedule()
  },
}))

/** Черновик канала: как useState(''), только переживает смену канала и перезагрузку. */
export function useDraft(channelId: string): [string, (text: string) => void] {
  const text = useDraftsStore((s) => s.drafts[channelId] ?? '')
  const setDraft = useDraftsStore((s) => s.setDraft)
  return [text, useCallback((next: string) => setDraft(channelId, next), [channelId, setDraft])]
}

/** Подстановки упоминаний канала для useMentions — живут вместе с черновиком. */
export function draftPicks(channelId: string): Map<string, string> {
  let map = picks.get(channelId)
  if (!map) {
    map = new Map()
    picks.set(channelId, map)
  }
  return map
}

type Stored = [channelId: string, text: string, picks?: [string, string][]]

function load(userId: string | null) {
  owner = userId
  picks.clear()
  const drafts: Record<string, string> = {}
  if (userId) {
    try {
      const list: unknown = JSON.parse(localStorage.getItem(KEY + userId) ?? '[]')
      for (const item of Array.isArray(list) ? (list as unknown[]) : []) {
        if (!Array.isArray(item) || typeof item[0] !== 'string' || typeof item[1] !== 'string' || !item[1]) continue
        drafts[item[0]] = item[1]
        const pairs = Array.isArray(item[2]) ? (item[2] as unknown[]).filter((p): p is [string, string] => Array.isArray(p) && typeof p[0] === 'string' && typeof p[1] === 'string') : []
        if (pairs.length) picks.set(item[0], new Map(pairs))
      }
    } catch {
      /* приватный режим или испорченная запись — начинаем с чистого листа */
    }
  }
  useDraftsStore.setState({ drafts })
}

let timer: ReturnType<typeof setTimeout> | null = null

/** Пишем не на каждую букву, а раз в полсекунды набора. */
function schedule() {
  if (timer === null) timer = setTimeout(flush, 500)
}

function flush() {
  if (timer !== null) clearTimeout(timer)
  timer = null
  if (!owner) return
  const entries = Object.entries(useDraftsStore.getState().drafts)
  const out: Stored[] = []
  let total = 0
  // с самых свежих: при переполнении теряются давние
  for (let i = entries.length - 1; i >= 0 && out.length < MAX_CHANNELS; i--) {
    const [channelId, text] = entries[i]!
    total += text.length
    if (total > MAX_CHARS) break
    const used = [...(picks.get(channelId) ?? [])].filter(([readable]) => text.includes(readable))
    out.unshift(used.length ? [channelId, text, used] : [channelId, text])
  }
  try {
    if (out.length) localStorage.setItem(KEY + owner, JSON.stringify(out))
    else localStorage.removeItem(KEY + owner)
  } catch {
    /* хранилище переполнено или запрещено: черновик доживёт до перезагрузки */
  }
}

load(useAuthStore.getState().user?.id ?? null)
// другая учётная запись — свои черновики; прежние сначала дописываем
useAuthStore.subscribe((s, prev) => {
  if (s.user?.id === prev.user?.id) return
  flush()
  load(s.user?.id ?? null)
})
// вкладку закрывают или телефон уводит её в фон — дописываем, не дожидаясь таймера
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
}
